import { connectSsr } from '@bart.sk-eshop/ssr-service';
import { connect } from 'react-redux';
import * as React from 'react';
import { __ } from '@bart.sk-eshop/react-i18n';
import styled, { css } from 'styled-components';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { InputStyled } from '../../components/_helpers/form/Input';
import { rem } from 'polished';
import { Clearfix } from '../../theme/libraries/h5b-helpers';
import { ButtonStyled } from '../../components/_helpers/form/Button';
import { Form, Field, FormSpy } from 'react-final-form';
import Alert from '../../components/Alert/Alert';
import EmailAutocomplete from '../../components/_helpers/form/EmailAutocomplete';
import API from '../../services/API';
import { composeValidators, email, required } from '../../utilities/validators';
import { Loader } from '../../components/_helpers/Loader/Loader';
import { scrollToError } from '../../utilities';
import {
  ErrorPopup,
  InputStyledStyles,
} from '../../components/_helpers/form/FormStyles';
import { browserHistory } from 'react-router';
import { URL_WITHDRAWAL_FORM_THANK_YOU } from '../../constants';

const WithdrawalLabel = styled.div`
  margin-bottom: ${rem(5)};
  font-weight: 500;
`;

const WithdrawalInputCss: any = css`
  display: block;
  width: 100%;

  ${(props: any) => {
    return props.error ? 'border:1px solid red;' : '';
  }}
`;

const WithdrawalInput: any = styled(InputStyled)`
  ${WithdrawalInputCss};
`;

const WithdrawalInputEmail: any = styled(EmailAutocomplete)`
  ${WithdrawalInputCss};
`;

const WithdrawalDatePicker: any = styled(DatePicker)`
  ${InputStyledStyles};
  display: block;
  width: 100%;

  ${(props: any) => {
    return props.error ? 'border:1px solid red;' : '';
  }}
`;

const WithdrawalTextarea = styled.textarea`
  ${WithdrawalInputCss};
  min-height: ${rem(120)};
  height: 100%;
  padding: ${rem(10)};
  font-family: inherit;
  font-size: ${rem(14)};
  border: 1px solid #ccc;
  border-radius: ${rem(4)};
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const WithdrawalRow = styled(Clearfix)`
  margin-right: ${rem(-5)};
  margin-bottom: ${rem(20)};

  ${({ theme }) => theme.media('xs')} {
    &.email {
      width: 100%;
      max-width: ${rem(600)};
    }
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(20)};
  margin-bottom: ${rem(20)};

  ${({ theme }) => theme.media('md')} {
    grid-template-columns: 1fr 1fr;
  }
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const Submit = styled(ButtonStyled)`
  display: block;
  width: 100%;
  margin-top: ${rem(20)};
`;

const WithdrawalFormStyled = styled.form`
  width: ${rem(800)};
  max-width: 100%;
`;

/*
const SectionTitle = styled.h3`
  font-size: ${rem(20)};
  margin-bottom: ${rem(15)};
  font-weight: 600;
`;
*/

const AlertWrapper = styled.div`
  margin-top: ${rem(20)};
`;

const RefundOption = styled.label`
  display: flex;
  align-items: flex-start;
  gap: ${rem(8)};
  margin-bottom: ${rem(10)};
  cursor: pointer;
  font-weight: 400;

  input {
    margin-top: ${rem(4)};
  }
`;

const IbanInputWrapper = styled.div`
  margin-top: ${rem(10)};
  margin-left: ${rem(24)};
`;

const RefundInfoText = styled.p`
  margin-top: ${rem(-10)};
  margin-bottom: 0;
  font-size: ${rem(13)};
  color: #666;
`;

interface WithdrawalFormProps {
  globalMetaTags: any;
}

const WithdrawalForm = (props: WithdrawalFormProps) => {
  const [emailStatus, setEmailStatus] = React.useState(0);
  const [sendingEmail, setSendingEmail] = React.useState(false);

  const onSubmit = async (values) => {
    const copyValues = JSON.parse(JSON.stringify(values));

    if (copyValues.refundMethod === 'same') {
      copyValues.refundMethodLabel = __(
        'Platbu žiadam vrátiť rovnakým spôsobom, akým bola uhradená.',
      );
      copyValues.bankAccount = '';
    } else if (copyValues.refundMethod === 'iban') {
      copyValues.refundMethodLabel = __(
        'Platbu žiadam vrátiť na bankový účet (IBAN): {iban}',
      ).replace('{iban}', copyValues.bankAccount || '');
    }

    setSendingEmail(true);

    try {
      await API.withdrawalRequest(
        {
          xAcceptLanguage: 'sk',
        },
        copyValues,
      );

      browserHistory.push(URL_WITHDRAWAL_FORM_THANK_YOU);
    } catch (e) {
      setEmailStatus(-1);
    }
    setSendingEmail(false);
  };

  return (
    <>
      <Loader loading={sendingEmail}>
        <div>
          {emailStatus === -1 && (
            <AlertWrapper>
              <Alert type="error">
                {' '}
                {__('Žiadosť sa nepodarilo odoslať')}{' '}
              </Alert>
            </AlertWrapper>
          )}
          <Form
            onSubmit={onSubmit}
            initialValues={{ refundMethod: 'same' }}
            // tslint:disable-next-line:jsx-no-lambda
            render={({ handleSubmit, values }) => (
              <WithdrawalFormStyled onSubmit={handleSubmit}>
                {/*
                  <SectionTitle>
                    {__('Týmto oznamujem, že odstupujem od zmluvy')}
                  </SectionTitle>
                  */}

                <Field name="withdrawalDetails">
                  {({ input, meta }) => (
                    <WithdrawalRow>
                      <WithdrawalTextarea
                        {...input}
                        placeholder={__(
                          'Uveďte podrobnosti o tovare/službe...',
                        )}
                        error={meta.error && meta.touched}
                      />
                      {meta.error && meta.touched && (
                        <ErrorPopup>{meta.error}</ErrorPopup>
                      )}
                    </WithdrawalRow>
                  )}
                </Field>

                {/* Riadok 1: Číslo objednávky | Dátum objednania */}
                <FormGrid>
                  <InputWrapper>
                    <Field name="orderNumber" validate={required}>
                      {({ input, meta }) => (
                        <>
                          <WithdrawalLabel>
                            {__('Číslo objednávky:')}
                          </WithdrawalLabel>
                          <WithdrawalInput
                            {...input}
                            error={meta.error && meta.touched}
                          />
                          {meta.error && meta.touched && (
                            <ErrorPopup>{meta.error}</ErrorPopup>
                          )}
                        </>
                      )}
                    </Field>
                  </InputWrapper>

                  <InputWrapper>
                    <Field name="orderDate">
                      {({ input, meta }) => (
                        <>
                          <WithdrawalLabel>
                            {__(
                              'Dátum objednania / dátum prijatia (nepovinné):',
                            )}
                          </WithdrawalLabel>
                          <WithdrawalDatePicker
                            selected={input.value}
                            onChange={input.onChange}
                            dateFormat="dd.MM.yyyy"
                            placeholderText="DD.MM.RRRR"
                            error={meta.error && meta.touched}
                          />
                          {meta.error && meta.touched && (
                            <ErrorPopup>{meta.error}</ErrorPopup>
                          )}
                        </>
                      )}
                    </Field>
                  </InputWrapper>
                </FormGrid>

                {/* Riadok 2: Meno a priezvisko | Email */}
                <FormGrid>
                  <InputWrapper>
                    <Field name="customerName" validate={required}>
                      {({ input, meta }) => (
                        <>
                          <WithdrawalLabel>
                            {__('Meno a priezvisko spotrebiteľa:')}
                          </WithdrawalLabel>
                          <WithdrawalInput
                            {...input}
                            error={meta.error && meta.touched}
                          />
                          {meta.error && meta.touched && (
                            <ErrorPopup>{meta.error}</ErrorPopup>
                          )}
                        </>
                      )}
                    </Field>
                  </InputWrapper>

                  <InputWrapper>
                    <Field
                      name="customerEmail"
                      validate={composeValidators(required, email)}
                    >
                      {({ input, meta }) => (
                        <>
                          <WithdrawalLabel>
                            {__('Email spotrebiteľa:')}
                          </WithdrawalLabel>
                          <WithdrawalInputEmail
                            {...input}
                            error={meta.error && meta.touched}
                          />
                          {meta.error && meta.touched && (
                            <ErrorPopup>{meta.error}</ErrorPopup>
                          )}
                        </>
                      )}
                    </Field>
                  </InputWrapper>
                </FormGrid>

                {/* Riadok 3: Adresa spotrebiteľa (celá šírka) */}
                <WithdrawalRow>
                  <Field name="customerAddress">
                    {({ input, meta }) => (
                      <>
                        <WithdrawalLabel>
                          {__('Adresa spotrebiteľa (nepovinné):')}
                        </WithdrawalLabel>
                        <WithdrawalInput
                          {...input}
                          error={meta.error && meta.touched}
                        />
                        {meta.error && meta.touched && (
                          <ErrorPopup>{meta.error}</ErrorPopup>
                        )}
                      </>
                    )}
                  </Field>
                </WithdrawalRow>

                {/* Riadok 4: Spôsob vrátenia platby (celá šírka) */}
                <WithdrawalRow>
                  <Field
                    name="refundMethod"
                    type="radio"
                    value="same"
                    validate={required}
                  >
                    {({ input }) => (
                      <RefundOption>
                        <input {...input} />
                        <span>
                          {__(
                            'Platbu žiadam vrátiť rovnakým spôsobom, akým bola uhradená.',
                          )}
                        </span>
                      </RefundOption>
                    )}
                  </Field>

                  <Field
                    name="refundMethod"
                    type="radio"
                    value="iban"
                    validate={required}
                  >
                    {({ input, meta }) => (
                      <>
                        <RefundOption>
                          <input {...input} />
                          <span>
                            {__('Platbu žiadam vrátiť na bankový účet (IBAN):')}
                          </span>
                        </RefundOption>
                        {meta.error && meta.touched && (
                          <ErrorPopup>{meta.error}</ErrorPopup>
                        )}
                      </>
                    )}
                  </Field>

                  {values.refundMethod === 'iban' && (
                    <IbanInputWrapper>
                      <Field name="bankAccount" validate={required}>
                        {({ input, meta }) => (
                          <>
                            <WithdrawalInput
                              {...input}
                              error={meta.error && meta.touched}
                              placeholder="SK00 0000 0000 0000 0000 0000"
                            />
                            {meta.error && meta.touched && (
                              <ErrorPopup>{meta.error}</ErrorPopup>
                            )}
                          </>
                        )}
                      </Field>
                    </IbanInputWrapper>
                  )}
                </WithdrawalRow>

                <WithdrawalRow>
                  <RefundInfoText>
                    {__(
                      'Platby Vám budú vrátené bez zbytočného odkladu a v každom prípade najneskôr do 14 dní odo dňa, keď nám bude doručené Vaše oznámenie o odstúpení od tejto kúpnej zmluvy. Úhrada bude uskutočnená rovnakým spôsobom, aký ste použili pri Vašej platbe, pokiaľ ste uvedením IBAN čísla Vášho bankového účtu nedali súhlas s úhradou na Váš bankový účet a to bez účtovania akýchkoľvek ďalších poplatkov.',
                    )}
                  </RefundInfoText>
                </WithdrawalRow>

                <WithdrawalRow>
                  <Submit type="submit" className="primary">
                    {__('Odoslať odstúpenie od zmluvy')}
                  </Submit>
                </WithdrawalRow>

                <FormSpy
                  subscription={{ submitFailed: true, modified: false }}
                  onChange={() => {
                    scrollToError();
                  }}
                />
              </WithdrawalFormStyled>
            )}
          />
        </div>
      </Loader>
    </>
  );
};

WithdrawalForm.getInitialProps = async (parentProps) => {
  const promises: any[] = [];

  await Promise.all(promises);
};

const mapStateToProps = (state) => {
  return {
    globalMetaTags: state.general.globalMetaTags,
  };
};

export default connect(mapStateToProps)(
  connectSsr({ displayName: 'WithdrawalForm' })(WithdrawalForm),
);
